import { unwrapAtsEnvelope } from '@/api/ats/atsEnvelope';
import { http } from '@/lib/ats-https';
import type {
  JobsListFilters,
  JobsListPage,
  JobsListSort,
  JobsListView,
} from '@/store/jobsList/useJobsListStore';

/**
 * The list endpoint's `view`. The jobs page sends its card/table view; the
 * copy-from picker sends one of the plan/description views instead.
 */
export type JobsListRequestView =
  | JobsListView
  | 'job_description'
  | 'template_interview_plan'
  | 'interview_plan';

/**
 * ATS endpoints can answer 200 with an application-level `errors` array rather
 * than an HTTP error, and the auth interceptor resolves some 4xx bodies too.
 * The RTK base query folded both into `{ error }` and callers branched on it;
 * TanStack has no equivalent, so `unwrapAtsEnvelope` raises instead —
 * otherwise a failed write looks like a success.
 */

/**
 * What a mutation resolves with. Only the fields callers actually read are
 * named; without this every mutation resolves as `unknown` and each caller has
 * to assert before reading `id`.
 */
export interface AtsJob {
  id?: string | number;
  name?: string;
  confidential?: boolean;
  /** Absent, not 0, when `t_close_job_bulk_reject` is off for the account. */
  rejectable_candidates_count?: number;
  [key: string]: unknown;
}

export interface JobsListParams {
  view?: JobsListRequestView;
  sort_by?: Record<string, string>;
  filter_by?: Record<string, unknown>;
  q?: string | null;
  limit?: number;
  offset?: number;
}

/** A list row. Only the fields the pickers read are named. */
export interface JobsListJob {
  id?: string | number;
  name?: string;
  stages?: { name?: string; [key: string]: unknown }[];
  [key: string]: unknown;
}

export interface JobsListResponse {
  ats_jobs?: JobsListJob[];
  [key: string]: unknown;
}

interface IdLike {
  id: string | number;
}

/**
 * Builds the list request params from view state.
 *
 * Lifted verbatim from `fetchJobsWithFilter` in jobs-helpers so behaviour is
 * unchanged: `beConfig` carries condition objects (`{ is: [392] }`) and is
 * spread over the legacy id-array format, then empties are stripped.
 */
export const buildJobsListParams = ({
  currentView,
  filters,
  sort,
  page,
  searchTerm,
  type,
}: {
  currentView?: JobsListRequestView;
  filters?: Partial<JobsListFilters>;
  sort?: JobsListSort;
  page?: Partial<JobsListPage>;
  searchTerm?: string | null;
  type?: string;
}): JobsListParams => {
  const {
    office_ids = [],
    department_ids = [],
    job_statuses = [],
    user_ids = [],
    eeoc_data_collection = [],
    beConfig = {},
  } = (filters ?? {}) as Record<string, any>;

  const updatedJobStatus =
    type === 'jobStage'
      ? [{ id: 'published' }, { id: 'scheduled' }]
      : [...(job_statuses ?? [])];

  const filter_by: Record<string, unknown> = {
    office_ids: (office_ids as IdLike[]).map((i) => i.id),
    department_ids: (department_ids as IdLike[]).map((i) => i.id),
    job_statuses: (updatedJobStatus as IdLike[])?.map((i) => i.id),
    user_ids: (user_ids as IdLike[]).map((i) => i.id),
    eeoc_data_collection: (eeoc_data_collection as IdLike[])?.[0]?.id,
    ...beConfig,
  };

  Object.keys(filter_by).forEach((key) => {
    const value = filter_by[key];
    if (
      value === undefined ||
      value === null ||
      (Array.isArray(value) && value.length === 0)
    ) {
      delete filter_by[key];
    }
  });

  return {
    view: currentView,
    sort_by: sort?.element ? { [sort.element]: sort.order } : undefined,
    filter_by,
    q: searchTerm,
    limit: page?.limit,
    offset: page?.offset,
  };
};

export interface AtsJobStage {
  id: number;
  name?: string;
  position?: number;
  ats_job_milestone?: { name?: string | null } | null;
}

export const jobsApi = {
  list: async (params: JobsListParams): Promise<JobsListResponse> =>
    unwrapAtsEnvelope<JobsListResponse>(
      await http.get('/ats_jobs', { params })
    ),

  create: async (payload: Record<string, unknown>): Promise<AtsJob> =>
    unwrapAtsEnvelope<AtsJob>(await http.post('/ats_jobs', payload)),

  update: async ({
    jobId,
    payload,
  }: {
    jobId: string | number;
    payload: Record<string, unknown>;
  }): Promise<AtsJob> =>
    unwrapAtsEnvelope<AtsJob>(await http.put(`/ats_jobs/${jobId}`, payload)),

  clone: async ({
    jobId,
    payload,
  }: {
    jobId: string | number;
    payload?: Record<string, unknown>;
  }): Promise<AtsJob> =>
    unwrapAtsEnvelope<AtsJob>(
      await http.post(`/ats_jobs/${jobId}/clone`, payload ?? {})
    ),

  archive: async ({ jobId }: { jobId: string | number }): Promise<AtsJob> =>
    unwrapAtsEnvelope<AtsJob>(await http.put(`/ats_jobs/${jobId}/archive`, {})),

  close: async ({
    jobId,
    payload,
  }: {
    jobId: string | number;
    payload?: Record<string, unknown>;
  }): Promise<AtsJob> =>
    unwrapAtsEnvelope<AtsJob>(
      await http.put(`/ats_jobs/${jobId}/close`, payload ?? {})
    ),

  get: async ({ jobId }: { jobId: string | number }): Promise<AtsJob> =>
    unwrapAtsEnvelope<AtsJob>(await http.get(`/ats_jobs/${jobId}`)),

  stages: async ({
    jobId,
  }: {
    jobId: string | number;
  }): Promise<AtsJobStage[]> =>
    unwrapAtsEnvelope<AtsJobStage[]>(
      await http.get(`/ats_jobs/${jobId}/ats_job_stages`)
    ),

  reopen: async ({
    jobId,
    payload,
  }: {
    jobId: string | number;
    payload?: Record<string, unknown>;
  }): Promise<AtsJob> =>
    unwrapAtsEnvelope<AtsJob>(
      await http.put(`/ats_jobs/${jobId}/reopen`, payload ?? {})
    ),
};
